import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertTriangle, Loader2, RefreshCw, CheckCircle2, Plus, Undo2 } from "lucide-react";

const hhmm = (sec) => {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

const dur = (sec) => {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
};

const covCls = (pct) =>
  pct >= 99 ? "text-emerald-500" : pct >= 80 ? "text-amber-500" : "text-rose-500";
const barCls = (pct) =>
  pct >= 99 ? "bg-emerald-500" : pct >= 80 ? "bg-amber-500" : "bg-rose-500";

export function GapDetectorDialog({ open, onClose, onJumpToChannel }) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [minGap, setMinGap] = useState(300); // 5 min default
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const scan = async () => {
    setBusy(true);
    try {
      const r = await api.get("/schedule/gaps", { params: { date, min_gap_sec: minGap } });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan failed");
    }
    setBusy(false);
  };

  useEffect(() => {
    if (open) scan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const results = data?.results || [];
  const clean = results.filter((r) => r.gap_count === 0).length;

  const [filling, setFilling] = useState(null); // key of gap being filled
  const [lastFill, setLastFill] = useState(null); // { batch_id, label }
  const [undoing, setUndoing] = useState(false);

  const fillGap = async (r, g, key) => {
    setFilling(key);
    try {
      const res = await api.post("/schedule/fill-gap", {
        channel_id: r.channel_id,
        date: data.date,
        start_sec: g.start_sec,
        end_sec: g.end_sec,
      });
      setLastFill({ batch_id: res.data.batch_id, label: `${r.name} · ${res.data.blocks_created} block(s) · ${dur(res.data.seconds_filled)}` });
      toast.success(`${r.name} · filled ${res.data.blocks_created} block(s) · ${dur(res.data.seconds_filled)}`);
      await scan();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fill failed");
    }
    setFilling(null);
  };

  const fillAll = async (r) => {
    setFilling(`${r.channel_id}-all`);
    try {
      const batchId = (window.crypto?.randomUUID?.() || `fill-${Date.now()}`);
      let blocks = 0;
      for (const g of r.gaps) {
        const res = await api.post("/schedule/fill-gap", {
          channel_id: r.channel_id, date: data.date, start_sec: g.start_sec, end_sec: g.end_sec, batch_id: batchId,
        });
        blocks += res.data.blocks_created;
      }
      setLastFill({ batch_id: batchId, label: `${r.name} · ${r.gap_count} gap(s) · ${blocks} block(s)` });
      toast.success(`${r.name} · filled ${r.gap_count} gap(s) · ${blocks} block(s)`);
      await scan();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fill failed");
    }
    setFilling(null);
  };

  const undoLastFill = async () => {
    if (!lastFill) return;
    setUndoing(true);
    try {
      const res = await api.post("/schedule/undo-fill", { batch_id: lastFill.batch_id });
      toast.success(`Undone · removed ${res.data.deleted} block(s)`);
      setLastFill(null);
      await scan();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Undo failed");
    }
    setUndoing(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-card border-border rounded-sm max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="gap-detector-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400" /> Cross-channel gap detector
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Scans every channel for uncovered blocks (dead air) on the selected day. Channels with the least coverage float to the top.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-end gap-3 border-b border-border pb-3">
          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Date</label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 rounded-sm h-9 text-xs w-40" data-testid="gap-date" />
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Ignore gaps under</label>
            <select
              value={minGap}
              onChange={(e) => setMinGap(parseInt(e.target.value, 10))}
              className="mt-1 rounded-sm h-9 text-xs bg-background border border-border px-2 block"
              data-testid="gap-mingap"
            >
              <option value={60}>1 min</option>
              <option value={300}>5 min</option>
              <option value={600}>10 min</option>
              <option value={1800}>30 min</option>
            </select>
          </div>
          <Button size="sm" className="rounded-sm h-9 text-xs tenant-btn" onClick={scan} disabled={busy} data-testid="gap-rescan">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />} Rescan
          </Button>
          {data && (
            <div className="ml-auto text-[11px] font-mono text-muted-foreground">
              <span className="text-rose-400 font-bold">{data.channels_with_gaps}</span> with gaps ·{" "}
              <span className="text-emerald-400 font-bold">{clean}</span> clean · {data.channel_count} total
            </div>
          )}
        </div>

        {lastFill && (
          <div className="flex items-center gap-2 rounded-sm border border-emerald-400/40 bg-emerald-500/10 px-3 py-2 mt-1" data-testid="gap-undo-bar">
            <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
            <span className="text-[11px] font-mono text-emerald-300 truncate">Filled: {lastFill.label}</span>
            <Button
              size="sm" variant="outline"
              className="ml-auto rounded-sm h-7 text-xs border-amber-500/50 text-amber-400 hover:bg-amber-500/15"
              onClick={undoLastFill}
              disabled={undoing}
              data-testid="gap-undo-btn"
            >
              {undoing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Undo2 className="w-3.5 h-3.5 mr-1" />} Undo last fill
            </Button>
          </div>
        )}

        <div className="space-y-2 mt-1">
          {busy && !data && (
            <div className="text-center py-10 text-xs text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Scanning channels…
            </div>
          )}
          {data && results.length === 0 && (
            <div className="text-center py-10 text-xs text-muted-foreground">No channels found.</div>
          )}
          {results.map((r) => (
            <div
              key={r.channel_id}
              className={`border rounded-sm p-3 ${r.gap_count ? "border-rose-500/30 bg-rose-500/[0.03]" : "border-border"}`}
              data-testid={`gap-row-${r.slug || r.channel_id}`}
            >
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: r.color || "#888" }} />
                <button
                  className="font-display font-bold text-sm hover:text-fuchsia-400 text-left"
                  onClick={() => onJumpToChannel?.(r.channel_id)}
                  data-testid={`gap-jump-${r.slug || r.channel_id}`}
                  title="Open this channel in the grid"
                >
                  {r.name}
                </button>
                {r.is_exception && (
                  <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 rounded-sm bg-fuchsia-500/20 text-fuchsia-400">exception day</span>
                )}
                {r.overlaps > 0 && (
                  <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 rounded-sm bg-amber-500/20 text-amber-400">{r.overlaps} overlap</span>
                )}
                <span className={`ml-auto font-mono text-xs font-bold ${covCls(r.coverage_pct)}`}>
                  {r.gap_count === 0 ? <CheckCircle2 className="w-3.5 h-3.5 inline mr-1" /> : null}
                  {r.coverage_pct}%
                </span>
              </div>

              <div className="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
                <div className={`h-full ${barCls(r.coverage_pct)}`} style={{ width: `${r.coverage_pct}%` }} />
              </div>

              {r.gap_count > 0 && (
                <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid={`gap-windows-${r.slug || r.channel_id}`}>
                  {r.gaps.map((g, i) => {
                    const key = `${r.channel_id}-${g.start_sec}`;
                    const busy = filling === key || filling === `${r.channel_id}-all`;
                    return (
                      <button
                        key={i}
                        onClick={() => fillGap(r, g, key)}
                        disabled={!!filling}
                        className="group text-[10px] font-mono px-2 py-0.5 rounded-sm bg-rose-500/10 text-rose-400 border border-rose-500/30 hover:bg-emerald-500/15 hover:text-emerald-400 hover:border-emerald-400/40 transition-colors disabled:opacity-50 flex items-center gap-1"
                        title={`Fill this ${dur(g.duration_sec)} gap with rotating category content`}
                        data-testid={`gap-fill-${r.slug || r.channel_id}-${g.start_sec}`}
                      >
                        {busy ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Plus className="w-2.5 h-2.5 opacity-60 group-hover:opacity-100" />}
                        {hhmm(g.start_sec)}–{hhmm(g.end_sec)} · {dur(g.duration_sec)}
                      </button>
                    );
                  })}
                  <span className="text-[10px] font-mono text-muted-foreground self-center ml-1">
                    {dur(r.total_gap_sec)} uncovered
                  </span>
                  <button
                    onClick={() => fillAll(r)}
                    disabled={!!filling}
                    className="ml-auto text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm bg-emerald-500/15 text-emerald-400 border border-emerald-400/40 hover:bg-emerald-500/25 transition-colors disabled:opacity-50 flex items-center gap-1"
                    data-testid={`gap-fill-all-${r.slug || r.channel_id}`}
                  >
                    {filling === `${r.channel_id}-all` ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Plus className="w-2.5 h-2.5" />} Fill all
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
